import { IonCardContent, IonCol, IonRow, IonText } from '@ionic/react';

// Components
import {
  JointCouplingGraph,
  ScissorsColumnChart,
  ScissorsGraphCard,
  StanceCoordinativeVariabilityGraph,
  StanceLineGraph,
  ThighBarGraph,
} from '@/pages/ReportV2/components/Graphs';

import { Card } from '@/components/Cards';

import { ScissorsReportData } from '@/models/reportV2.model';

type AttractorsReportProps = {
  tabData: ScissorsReportData;
};

/** Run kinematics report tab
 * @param report - the full run report info and data
 */
export const RunScissorsReportTab = ({ tabData }: AttractorsReportProps) => {
  if (!tabData) {
    return <IonText>Data not found.</IonText>;
  }
  return (
    <>
      <IonRow className='ion-align-items-stretch ion-justify-content-center'>
        <IonCol className='' size='4'>
          <ScissorsGraphCard
            colors={tabData.scissorsData_Left.colors}
            series={tabData.scissorsData_Left.graphData}
            title={'Scissors Left'}
            x={tabData.scissorsData_Left.firstMarker}
            x2={tabData.scissorsData_Left.secondMarker}
          />
        </IonCol>
        <IonCol className='' size='4'>
          <ScissorsGraphCard
            colors={tabData.scissorsData_Right.colors}
            series={tabData.scissorsData_Right.graphData}
            title={'Scissors Right'}
            x={tabData.scissorsData_Right.firstMarker}
            x2={tabData.scissorsData_Right.secondMarker}
          />
        </IonCol>
        <IonCol className='' size='4'>
          <ScissorsColumnChart
            data={tabData.scissorsBarGraphData}
            title={'Scissors velocity'}
            yAxisLabel={tabData.hamstringData.yAxisLabelVelocity}
          />
        </IonCol>
      </IonRow>
      <IonRow className='ion-align-items-stretch ion-justify-content-center'>
        <IonCol className='' size='4'>
          <JointCouplingGraph
            data={tabData.runJointCouplingData.seriesJointCoupling_Left}
            title={'Hip / Knee coupling Left'}
            xMax={tabData.runJointCouplingData.jointCouplingAxisRange.xMaxValue}
            xMin={tabData.runJointCouplingData.jointCouplingAxisRange.xMinValue}
            xRange={tabData.runJointCouplingData.jointCouplingAxisRange.xTickAmount}
            zoomModalContent={tabData.runJointCouplingData.jointCouplingZoomModalContent}
          />
        </IonCol>
        <IonCol className='' size='4'>
          <JointCouplingGraph
            data={tabData.runJointCouplingData.seriesJointCoupling_Right}
            title={'Hip / Knee coupling Right'}
            xMax={tabData.runJointCouplingData.jointCouplingAxisRange.xMaxValue}
            xMin={tabData.runJointCouplingData.jointCouplingAxisRange.xMinValue}
            xRange={tabData.runJointCouplingData.jointCouplingAxisRange.xTickAmount}
            zoomModalContent={tabData.runJointCouplingData.jointCouplingZoomModalContent}
          />
        </IonCol>
        <IonCol className='' size='4'>
          <ThighBarGraph
            data={tabData.hamstringData.thighVelocityBarData}
            stdData={tabData.hamstringData.thighVelocityBarData_std}
            title={'Hamstring propulsion - Velocity'}
            xAxisCategories={['Peak TSw', 'AV. TSw', 'Peak TSt', 'Av. Stance']}
            yAxisLabel={tabData.hamstringData.yAxisLabelVelocity}
          />
        </IonCol>
      </IonRow>
      <IonRow className='ion-align-items-stretch ion-justify-content-center'>
        <IonCol className='' size='4'>
          <StanceCoordinativeVariabilityGraph
            HOAnnotation={
              tabData.coordinativeVariabilityData.coordinativeVariabilityData_Left.annotations.HOAnnotation_Left
            }
            ICAnnotation={
              tabData.coordinativeVariabilityData.coordinativeVariabilityData_Left.annotations.ICAnnotation_Left
            }
            MStAnnotation={
              tabData.coordinativeVariabilityData.coordinativeVariabilityData_Left.annotations.MStAnnotation_Left
            }
            TOAnnotation={
              tabData.coordinativeVariabilityData.coordinativeVariabilityData_Left.annotations.TOAnnotation_Left
            }
            data={tabData.coordinativeVariabilityData.coordinativeVariabilityData_Left.series}
            subtitle={'Pelvis / Thigh (s/s & c/t)'}
            title={'Coordinative Variability Hip Left'}
          />
        </IonCol>
        <IonCol className='' size='4'>
          <StanceCoordinativeVariabilityGraph
            HOAnnotation={
              tabData.coordinativeVariabilityData.coordinativeVariabilityData_Right.annotations.HOAnnotation_Right
            }
            ICAnnotation={
              tabData.coordinativeVariabilityData.coordinativeVariabilityData_Right.annotations.ICAnnotation_Right
            }
            MStAnnotation={
              tabData.coordinativeVariabilityData.coordinativeVariabilityData_Right.annotations.MStAnnotation_Right
            }
            TOAnnotation={
              tabData.coordinativeVariabilityData.coordinativeVariabilityData_Right.annotations.TOAnnotation_Right
            }
            data={tabData.coordinativeVariabilityData.coordinativeVariabilityData_Right.series}
            subtitle={'Pelvis / Thigh (s/s & c/t)'}
            title={'Coordinative Variability Hip Right'}
          />
        </IonCol>
        <IonCol className='' size='4'>
          <ThighBarGraph
            data={tabData.hamstringData.thighAccelerationBarData}
            stdData={tabData.hamstringData.thighAccelerationBarData_std}
            title={'Hamstring propulsion - Acceleration'}
            xAxisCategories={['TSw', 'Stance']}
            yAxisLabel={tabData.hamstringData.yAxisLabelAcceleration}
          />
        </IonCol>
      </IonRow>
      <IonRow className='ion-align-items-stretch ion-justify-content-center ion-padding-bottom'>
        <IonCol className='' size='4'>
          <StanceLineGraph
            HOAnnotation={
              tabData.coordinativeVariabilityData.coordinativeVariabilityData_Left.annotations.HOAnnotation_Left
            }
            ICAnnotation={
              tabData.coordinativeVariabilityData.coordinativeVariabilityData_Left.annotations.ICAnnotation_Left
            }
            MStAnnotation={
              tabData.coordinativeVariabilityData.coordinativeVariabilityData_Left.annotations.MStAnnotation_Left
            }
            TOAnnotation={
              tabData.coordinativeVariabilityData.coordinativeVariabilityData_Left.annotations.TOAnnotation_Left
            }
            series={tabData.swingLegRetractionData.thighAccelerationSeriesData_Left}
            subtitle={'Propulsion in stance - Acceleration'}
            title={'Swing Leg Retraction Left'}
            yAxisLabel={tabData.hamstringData.yAxisLabelAcceleration}
            yAxisMax={tabData.swingLegRetractionData.yMaxValue}
            yAxisMin={tabData.swingLegRetractionData.yMinValue}
          />
        </IonCol>
        <IonCol className='' size='4'>
          <StanceLineGraph
            HOAnnotation={
              tabData.coordinativeVariabilityData.coordinativeVariabilityData_Right.annotations.HOAnnotation_Right
            }
            ICAnnotation={
              tabData.coordinativeVariabilityData.coordinativeVariabilityData_Right.annotations.ICAnnotation_Right
            }
            MStAnnotation={
              tabData.coordinativeVariabilityData.coordinativeVariabilityData_Right.annotations.MStAnnotation_Right
            }
            TOAnnotation={
              tabData.coordinativeVariabilityData.coordinativeVariabilityData_Right.annotations.TOAnnotation_Right
            }
            series={tabData.swingLegRetractionData.thighAccelerationSeriesData_Right}
            subtitle={'Propulsion in stance - Acceleration'}
            title={'Swing Leg Retraction Right'}
            yAxisLabel={tabData.hamstringData.yAxisLabelAcceleration}
            yAxisMax={tabData.swingLegRetractionData.yMaxValue}
            yAxisMin={tabData.swingLegRetractionData.yMinValue}
          />
        </IonCol>
        <IonCol className='' size='4' style={{ height: '100%' }}>
          <Card style={{ margin: 0 }}>
            <IonCardContent>
              <table className='ORYX_Table_Scroll'>
                <thead>
                  <tr>
                    <th>&nbsp;</th>
                    <th>Left</th>
                    <th>Right</th>
                    <th>Symmetry</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className='ORYX_Table_Head'>
                    <td className='ORYX_Table_Scroll_SectionHeader' colSpan={4}>
                      Scissors
                    </td>
                  </tr>
                  <tr>
                    <td>Average velocity</td>
                    <td>
                      {tabData.scissorsTableData.scissors.averageVelocity.left +
                        ' ' +
                        tabData.scissorsTableData.scissors.averageVelocity.unit}
                    </td>
                    <td>
                      {tabData.scissorsTableData.scissors.averageVelocity.right +
                        ' ' +
                        tabData.scissorsTableData.scissors.averageVelocity.unit}
                    </td>
                    <td>{tabData.scissorsTableData.scissors.averageVelocity.symmetry + ' %'}</td>
                  </tr>
                  <tr className='ORYX_Table_Head'>
                    <td className='ORYX_Table_Scroll_SectionHeader' colSpan={4}>
                      Average Velocities
                    </td>
                  </tr>
                  <tr className=''>
                    <td>Terminal Swing</td>
                    <td>
                      {tabData.scissorsTableData.averageVelocities.terminalSwing.left +
                        ' ' +
                        tabData.scissorsTableData.averageVelocities.terminalSwing.unit}
                    </td>
                    <td>
                      {tabData.scissorsTableData.averageVelocities.terminalSwing.right +
                        ' ' +
                        tabData.scissorsTableData.averageVelocities.terminalSwing.unit}
                    </td>
                    <td>{tabData.scissorsTableData.averageVelocities.terminalSwing.symmetry + ' %'}</td>
                  </tr>
                  <tr className=''>
                    <td>Stance</td>
                    <td>
                      {tabData.scissorsTableData.averageVelocities.stance.left +
                        ' ' +
                        tabData.scissorsTableData.averageVelocities.stance.unit}
                    </td>
                    <td>
                      {tabData.scissorsTableData.averageVelocities.stance.right +
                        ' ' +
                        tabData.scissorsTableData.averageVelocities.stance.unit}
                    </td>
                    <td>{tabData.scissorsTableData.averageVelocities.stance.symmetry + ' %'}</td>
                  </tr>
                  <tr className='ORYX_Table_Head'>
                    <td className='ORYX_Table_Scroll_SectionHeader' colSpan={4}>
                      Peak velocities and accelerations
                    </td>
                  </tr>
                  <tr className=''>
                    <td>Velocity Terminal Swing</td>
                    <td>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.terminalSwing.left +
                        ' ' +
                        tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.terminalSwing.unit}
                    </td>
                    <td>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.terminalSwing.right +
                        ' ' +
                        tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.terminalSwing.unit}
                    </td>
                    <td>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.terminalSwing.symmetry + ' %'}
                    </td>
                  </tr>
                  <tr className=''>
                    <td> Velocity Terminal Stance</td>
                    <td>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.stance.left +
                        ' ' +
                        tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.stance.unit}
                    </td>
                    <td>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.stance.right +
                        ' ' +
                        tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.stance.unit}
                    </td>
                    <td>{tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.stance.symmetry + ' %'}</td>
                  </tr>
                  <tr className=''>
                    <td>Acceleration Terminal Swing</td>
                    <td>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.terminalSwing.left +
                        ' ' +
                        tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.terminalSwing.unit}
                    </td>
                    <td>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.terminalSwing.right +
                        ' ' +
                        tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.terminalSwing.unit}
                    </td>
                    <td>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.terminalSwing.symmetry +
                        ' %'}
                    </td>
                  </tr>
                  <tr className=''>
                    <td>Acceleration Stance</td>
                    <td>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.stance.left +
                        ' ' +
                        tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.stance.unit}
                    </td>
                    <td>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.stance.right +
                        ' ' +
                        tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.stance.unit}
                    </td>
                    <td>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.stance.symmetry + ' %'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </IonCardContent>
          </Card>
        </IonCol>
      </IonRow>
    </>
  );
};
